/**
 * Pattern-transform wiring for MappingPhase (column-mapping-ui spec, "Per-column
 * placeholder patterns with inline fan-out" / "Pattern editor with live preview").
 *
 * Live preview, match-count, and unmatched-sample warnings all go through the
 * shared, ReDoS-safe pattern engine in `@ooh/contracts` (`compilePattern` /
 * `applyPattern`) — the same pure logic the API dry-run and worker import
 * apply — so the editor's preview can never diverge from what actually gets
 * imported. Do NOT reimplement pattern matching here (that was the mockup's
 * throwaway `matchPattern`/`getPlaceholderNumbers`, kept out of apps/web).
 *
 * Extracted from mapping-phase.tsx to keep that file under the ESLint
 * max-lines limit (see mapping-phase-table.tsx:1-4 for the established
 * precedent of splitting this component tree).
 */
import * as React from 'react';
import { compilePattern, applyPattern } from '@ooh/contracts';
import type { ColumnTransformSuggestion, FieldOptionItem } from '@ooh/contracts';
import {
  PatternEditorDialog,
  type PatternEditorLabels,
  type PatternMatch,
} from './mapping-transforms-pattern-editor-dialog';
import type { TargetFieldSelectLabels } from './mapping-transforms-target-field-select';
import type { TransformMode } from './mapping-transforms-mode-toggle';
import type { ColumnMappingItem, MappingPhaseLabels } from './mapping-phase';

/** Unique placeholder numbers in the pattern, in order of appearance. Empty when the pattern has no `{n}` placeholders. */
export function getPatternPlaceholderNumbers(pattern: string): number[] {
  const order = compilePattern(pattern)?.placeholderOrder ?? [];
  return Array.from(new Set(order));
}

/** Adapts the shared applyPattern() to PatternEditorDialog's MatchPatternFn shape. */
export function matchPatternAgainstSample(pattern: string, sample: string): PatternMatch[] | null {
  const result = applyPattern(sample, { type: 'pattern', pattern, targets: [] });
  if (!result.matched) return null;
  return Array.from(result.valuesByPlaceholder.entries()).map(([index, value]) => ({
    index,
    value,
  }));
}

export interface PatternMatchStats {
  matchCount: number;
  totalCount: number;
  mismatchExamples: string[];
}

/** Match count / mismatch list across a column's sampled values ("Match count reflects non-matching rows"). */
export function computePatternMatchStats(pattern: string, samples: string[]): PatternMatchStats {
  if (compilePattern(pattern) === null) {
    return { matchCount: 0, totalCount: samples.length, mismatchExamples: [] };
  }
  const mismatchExamples: string[] = [];
  let matchCount = 0;
  for (const sample of samples) {
    if (applyPattern(sample, { type: 'pattern', pattern, targets: [] }).matched) {
      matchCount += 1;
    } else {
      mismatchExamples.push(sample);
    }
  }
  return { matchCount, totalCount: samples.length, mismatchExamples };
}

/** True when at least one sampled value doesn't match ("Column with an unmatched sample shows a warning indicator"). */
export function columnHasUnmatchedSample(pattern: string, samples: string[]): boolean {
  if (samples.length === 0 || compilePattern(pattern) === null) return false;
  return samples.some((s) => !applyPattern(s, { type: 'pattern', pattern, targets: [] }).matched);
}

/** Captured value for one placeholder from the first available sample, or '' when unmatched/no samples. */
export function capturedValueForBlank(
  pattern: string,
  samples: string[],
  blankIndex: number,
): string {
  const [first] = samples;
  if (first == null) return '';
  const result = applyPattern(first, { type: 'pattern', pattern, targets: [] });
  return result.matched ? (result.valuesByPlaceholder.get(blankIndex) ?? '') : '';
}

/**
 * Applies a pattern-text edit: resizes `transform.targets` to the new
 * placeholder count (padding with null, truncating extras) while preserving
 * already-assigned targets. Clearing all placeholders drops the transform,
 * reverting the row to a plain 1:1 mapping.
 */
export function applyPatternTextChange(
  mappings: ColumnMappingItem[],
  rowId: string,
  patternText: string,
): ColumnMappingItem[] {
  return mappings.map((m) => {
    if (m.id !== rowId) return m;
    const placeholderNumbers = getPatternPlaceholderNumbers(patternText);
    if (placeholderNumbers.length === 0) {
      // No placeholders left — drop the transform, reverting to a plain 1:1 mapping.
      return { id: m.id, sourceColumn: m.sourceColumn, mappedTo: m.mappedTo };
    }
    // Size by the MAXIMUM placeholder number, not the unique count — applyBlankTargetChange
    // and validate-row.ts#applyPattern both index targets[i] by placeholder NUMBER i+1, so a
    // non-contiguous/skip-numbered pattern like {1},{3} (2 unique placeholders, max number 3)
    // needs a length-3 array or a target assigned to {3} silently no-ops.
    const targetsLength = Math.max(...placeholderNumbers, 0);
    const prevTargets = m.transform?.type === 'pattern' ? m.transform.targets : [];
    const targets = Array.from({ length: targetsLength }, (_, i) => prevTargets[i] ?? null);
    return { ...m, transform: { type: 'pattern', pattern: patternText, targets } };
  });
}

/**
 * Applies the advisory pattern suggestion for a column the first time its editor is opened
 * (column-mapping-ui spec: "The pattern SHALL be fuzzy pre-filled from the samples"). A no-op
 * once the column already carries a transform, or when no suggestion exists for it.
 */
export function applySuggestedPatternOnFirstEdit(
  mappings: ColumnMappingItem[],
  rowId: string,
  transformSuggestions: ColumnTransformSuggestion[] | undefined,
): ColumnMappingItem[] {
  return mappings.map((m) => {
    if (m.id !== rowId || m.transform != null) return m;
    const suggestion = transformSuggestions?.find((s) => s.sourceColumn === m.sourceColumn);
    if (!suggestion) return m;
    return {
      ...m,
      transform: { type: 'pattern', pattern: suggestion.pattern, targets: suggestion.targets },
    };
  });
}

/**
 * Switches a column's transform mode between 'pattern' and 'valueMap' — the quiet in-dialog
 * affordance that makes value-map mode reachable (column-mapping-ui spec: "Constant and
 * value-map editors" — previously nothing in the UI ever set a `{type:'valueMap'}` transform).
 * Switching to 'valueMap' starts with an empty map (every sampled value flagged unmapped, per
 * spec). Switching back to 'pattern' drops the transform entirely, reverting to a plain 1:1
 * mapping — mirrors applyPatternTextChange's "no placeholders left" behavior.
 */
export function applyTransformModeSwitch(
  mappings: ColumnMappingItem[],
  rowId: string,
  mode: TransformMode,
): ColumnMappingItem[] {
  return mappings.map((m) => {
    if (m.id !== rowId) return m;
    // No-op guard: the mode toggle fires onModeChange even when that mode is already
    // active (its buttons aren't disabled based on current mode). Without this guard,
    // clicking the already-active label silently wipes the in-progress pattern/targets
    // or value-map entries back to a plain 1:1 mapping / empty map.
    if (m.transform?.type === mode) return m;
    if (mode === 'valueMap') {
      return { ...m, transform: { type: 'valueMap', map: {} } };
    }
    return { id: m.id, sourceColumn: m.sourceColumn, mappedTo: m.mappedTo };
  });
}

/** Applies a per-blank target-field change (1-based blankIndex, matching placeholder numbering). */
export function applyBlankTargetChange(
  mappings: ColumnMappingItem[],
  rowId: string,
  blankIndex: number,
  value: string | null,
): ColumnMappingItem[] {
  return mappings.map((m) => {
    if (m.id !== rowId || m.transform?.type !== 'pattern') return m;
    const targets = m.transform.targets.map((t, i) => (i === blankIndex - 1 ? value : t));
    return { ...m, transform: { ...m.transform, targets } };
  });
}

export interface MappingTransformLabels extends TargetFieldSelectLabels, PatternEditorLabels {
  editPatternAriaLabel: string;
  fanOutNoteLabel: string;
  unmatchedSampleWarningLabel: string;
  /** aria-label for the per-row "(i)" InfoHint that reveals a mapped field's description. */
  fieldInfoAriaLabel?: string;
}

/** Builds the combined table+dialog label bundle from the flat MappingPhaseLabels prop. */
export function buildMappingTransformLabels(labels: MappingPhaseLabels): MappingTransformLabels {
  return {
    skipLabel: labels.skipColumnOption,
    groupGeneral: labels.groupGeneral,
    groupPhysical: labels.groupPhysical,
    groupMuralFlags: labels.groupMuralFlags,
    groupDigital: labels.groupDigital,
    groupInternalCost: labels.groupInternalCost,
    groupClientPrice: labels.groupClientPrice,
    dialogTitle: labels.patternDialogTitle,
    columnLabelPrefix: labels.patternColumnLabelPrefix,
    dialogInfoLabel: labels.transformDialogInfoLabel,
    dialogInfoText: labels.transformDialogInfoText,
    sampleValueTemplate: labels.patternSampleValueTemplate,
    fieldSearchPlaceholder: labels.fieldSearchPlaceholder,
    fieldNoResults: labels.fieldNoResults,
    patternFieldLabel: labels.patternFieldLabel,
    autoDetectedNote: labels.patternAutoDetectedNote,
    patternPlaceholder: labels.patternPlaceholder,
    understandingLabel: labels.patternUnderstandingLabel,
    understandingInfoLabel: labels.patternUnderstandingInfoLabel,
    understandingInfoText: labels.patternUnderstandingInfoText,
    noPatternHint: labels.patternNoBlanksHint,
    matchedTemplate: labels.patternMatchedTemplate,
    noMatchText: labels.patternNoMatchText,
    showMismatchesLabel: labels.patternShowMismatchesLabel,
    hideMismatchesLabel: labels.patternHideMismatchesLabel,
    doneButtonLabel: labels.patternDoneButtonLabel,
    editPatternAriaLabel: labels.editPatternAriaLabel,
    fanOutNoteLabel: labels.fanOutNoteLabel,
    unmatchedSampleWarningLabel: labels.unmatchedSampleWarningLabel,
    fieldInfoAriaLabel: labels.fieldInfoAriaLabel,
    transformModePatternLabel: labels.transformModePatternLabel,
    transformModeValueMapLabel: labels.transformModeValueMapLabel,
    modeToggleInfoLabel: labels.modeToggleInfoLabel,
    modeToggleInfoText: labels.modeToggleInfoText,
  };
}

export interface PatternEditorHostProps {
  mapping: ColumnMappingItem;
  samples: string[];
  fieldOptions: FieldOptionItem[];
  labels: MappingTransformLabels;
  onPatternChange: (pattern: string) => void;
  onBlankTargetChange: (blankIndex: number, value: string | null) => void;
  onModeChange: (mode: TransformMode) => void;
  onClose: () => void;
}

/**
 * Renders the pattern editor dialog for one column. The caller MUST key this
 * component by `mapping.id` so the internal draft-pattern state (used only to
 * keep the live match-count in sync with each keystroke) resets cleanly
 * whenever a different column starts being edited.
 */
export function PatternEditorHost({
  mapping,
  samples,
  fieldOptions,
  labels,
  onPatternChange,
  onBlankTargetChange,
  onModeChange,
  onClose,
}: PatternEditorHostProps) {
  const transform = mapping.transform?.type === 'pattern' ? mapping.transform : undefined;
  const initialPattern = transform?.pattern ?? '';
  const initialTargets = transform?.targets ?? [];
  const [draftPattern, setDraftPattern] = React.useState(initialPattern);

  const stats = computePatternMatchStats(draftPattern, samples);

  const handlePatternChange = (pattern: string) => {
    setDraftPattern(pattern);
    onPatternChange(pattern);
  };

  return (
    <PatternEditorDialog
      sourceColumnLabel={mapping.sourceColumn}
      sampleValue={samples[0] ?? ''}
      initialPattern={initialPattern}
      initialBlankTargets={Object.fromEntries(initialTargets.map((t, i) => [i + 1, t]))}
      fieldOptions={fieldOptions}
      matchCount={stats.matchCount}
      totalCount={stats.totalCount}
      mismatchExamples={stats.mismatchExamples}
      matchPattern={matchPatternAgainstSample}
      getPlaceholderNumbers={getPatternPlaceholderNumbers}
      labels={labels}
      onPatternChange={handlePatternChange}
      onBlankTargetChange={onBlankTargetChange}
      onModeChange={onModeChange}
      onClose={onClose}
    />
  );
}

export interface MappingPatternEditorSlotProps {
  editingMapping: ColumnMappingItem | null;
  columnSamples: Record<string, string[]> | undefined;
  fieldOptions: FieldOptionItem[];
  labels: MappingTransformLabels;
  onPatternChange: (pattern: string) => void;
  onBlankTargetChange: (id: string, blankIndex: number, value: string | null) => void;
  /** Switches the column's transform mode (reachability affordance for value-map mode). */
  onTransformModeChange: (id: string, mode: TransformMode) => void;
  onClose: () => void;
}

/** Renders PatternEditorHost for the column currently being edited, or nothing. Kept as its own
 * component (rather than an inline `&&` in MappingPhase's JSX) so the null-check and the
 * per-column sample lookup don't add to MappingPhase's own cyclomatic complexity. */
export function MappingPatternEditorSlot({
  editingMapping,
  columnSamples,
  fieldOptions,
  labels,
  onPatternChange,
  onBlankTargetChange,
  onTransformModeChange,
  onClose,
}: MappingPatternEditorSlotProps) {
  // A column already switched to value-map mode is handled by MappingValueMapEditorSlot instead.
  if (editingMapping == null || editingMapping.transform?.type === 'valueMap') return null;
  return (
    <PatternEditorHost
      key={editingMapping.id}
      mapping={editingMapping}
      samples={columnSamples?.[editingMapping.sourceColumn] ?? []}
      fieldOptions={fieldOptions}
      labels={labels}
      onPatternChange={onPatternChange}
      onBlankTargetChange={(blankIndex, value) =>
        onBlankTargetChange(editingMapping.id, blankIndex, value)
      }
      onModeChange={(mode) => onTransformModeChange(editingMapping.id, mode)}
      onClose={onClose}
    />
  );
}
